import React from 'react';
import { CropZone, Farm, Field } from './types';
import { getFarmCropZoneIds, getFieldCropZoneIds } from './helpers';
import { CropZoneTitleItem, FarmTitleItem, FieldTitleItem } from './titleItems';

interface NodeOptions {
  geometryMap?: Map<string, any>;
  searchTerm?: string;
  searchCountMap?: Record<string, number>;
  fieldCzCountMap?: Record<string, number>;
  keyPrefix?: string;
  isCropGroupMode?: boolean;
}

const buildCropZoneNode = (
  cz: CropZone,
  checkedSet: Set<string>,
  onToggleCropZone: (id: string) => void,
  options: NodeOptions
) => {
  const isCzChecked = checkedSet.has(cz.id);
  return {
    key: cz.id,
    isLeaf: true,
    className: isCzChecked ? 'spv2-node--selected' : '',
    title: (
      <CropZoneTitleItem
        cropZone={cz}
        isChecked={isCzChecked}
        onToggle={onToggleCropZone}
        geometry={options.geometryMap?.get(cz.id) ?? null}
        searchTerm={options.searchTerm ?? ''}
        isCropGroupMode={options.isCropGroupMode ?? false}
      />
    )
  };
};

const buildFieldNode = (
  field: Field,
  checkedSet: Set<string>,
  onToggleField: (field: Field) => void,
  onToggleCropZone: (id: string) => void,
  onExpandKey: (key: string) => void,
  options: NodeOptions = {}
) => {
  const fieldCzIds = getFieldCropZoneIds(field);
  const fieldCheckedCount = fieldCzIds.filter(id => checkedSet.has(id)).length;
  const hasAllChecked =
    fieldCzIds.length > 0 && fieldCheckedCount === fieldCzIds.length;
  const hasPartialCheck =
    fieldCheckedCount > 0 && fieldCheckedCount < fieldCzIds.length;
  const selectedClass = hasAllChecked ? ' spv2-node--selected' : '';
  const nodeKey = options.keyPrefix
    ? `${options.keyPrefix}:${field.id}`
    : field.id;

  return {
    key: nodeKey,
    className: `spv2-field-node${selectedClass}`,
    title: (
      <FieldTitleItem
        field={field}
        nodeKey={nodeKey}
        isChecked={hasAllChecked}
        isIndeterminate={hasPartialCheck}
        onSelect={onToggleField}
        onExpand={onExpandKey}
        geometry={options.geometryMap?.get(field.id) ?? null}
        searchTerm={options.searchTerm ?? ''}
        totalCzCount={options.fieldCzCountMap?.[field.id]}
        isCropGroupMode={options.isCropGroupMode ?? false}
      />
    ),
    children: field.cropzones.map(cz =>
      buildCropZoneNode(cz, checkedSet, onToggleCropZone, options)
    )
  };
};

const buildFarmNode = (
  farm: Farm,
  checkedSet: Set<string>,
  onToggleFarm: (farm: Farm) => void,
  onToggleField: (field: Field) => void,
  onToggleCropZone: (id: string) => void,
  onExpandKey: (key: string) => void,
  options: NodeOptions = {}
) => {
  const farmCzIds = getFarmCropZoneIds(farm);
  const checkedCount = farmCzIds.filter(id => checkedSet.has(id)).length;
  const isChecked = farmCzIds.length > 0 && checkedCount === farmCzIds.length;
  const isIndeterminate = checkedCount > 0 && checkedCount < farmCzIds.length;
  const { searchTerm = '' } = options;
  const totalFieldCount = options.searchCountMap?.[farm.id];
  const farmNodeKey = options.keyPrefix
    ? `${options.keyPrefix}:${farm.id}`
    : farm.id;

  return {
    key: farmNodeKey,
    className: `spv2-farm-node${isChecked ? ' spv2-node--selected' : ''}`,
    title: (
      <FarmTitleItem
        farm={farm}
        nodeKey={farmNodeKey}
        isChecked={isChecked}
        isIndeterminate={isIndeterminate}
        onSelect={onToggleFarm}
        onExpand={onExpandKey}
        searchTerm={searchTerm}
        totalFieldCount={totalFieldCount}
        isCropGroupMode={options.isCropGroupMode ?? false}
      />
    ),
    children: farm.fields.map(field =>
      buildFieldNode(
        field,
        checkedSet,
        onToggleField,
        onToggleCropZone,
        onExpandKey,
        {
          ...options,
          keyPrefix: options.keyPrefix === undefined ? undefined : farmNodeKey
        }
      )
    )
  };
};

export default buildFarmNode;
